import { useState, type FormEvent } from 'react';
import { registerSchema } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { enregistrerSession } from '../../session';
import { naviguer, CHEMIN_CONFIDENTIALITE } from '../../routeur';
import { Champ } from '../composants/Champ';
import { Alerte, CadreAuth, LienTexte } from '../composants/CadreAuth';

export function Inscription({
  onInscrit,
  versConnexion,
}: {
  onInscrit: () => void;
  versConnexion: () => void;
}) {
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi) return;

    // Le MEME schema que le serveur : la longueur minimale du mot de passe ne peut pas
    // diverger entre ce que l'ecran annonce et ce que l'API accepte.
    const verifie = registerSchema.safeParse({ displayName: nom, email, password: motDePasse });
    if (!verifie.success) {
      setErreur(verifie.error.issues[0]?.message ?? 'Vérifie tes informations');
      return;
    }

    setEnvoi(true);
    setErreur(null);
    try {
      const reponse = await api.inscription(
        verifie.data.displayName,
        verifie.data.email,
        verifie.data.password,
      );
      enregistrerSession(reponse.token, reponse.user);
      onInscrit();
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  return (
    <CadreAuth
      titre="Bienvenue chez Colette"
      sousTitre="Quelques secondes, et elle commence à t'organiser."
      pied={
        <>
          Tu as déjà un compte ? <LienTexte onClick={versConnexion}>Se connecter</LienTexte>
        </>
      }
    >
      <form onSubmit={soumettre} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Champ
          libelle="Ton prénom et ton nom"
          autoComplete="name"
          valeur={nom}
          onChange={setNom}
          aide="C'est ainsi que Colette t'appellera."
        />
        <Champ
          libelle="Adresse email"
          type="email"
          inputMode="email"
          autoComplete="email"
          valeur={email}
          onChange={setEmail}
        />
        <Champ
          libelle="Mot de passe"
          type="password"
          autoComplete="new-password"
          valeur={motDePasse}
          onChange={setMotDePasse}
          aide="Au moins 10 caractères. Une phrase dont tu te souviens fait un très bon mot de passe."
        />

        {erreur !== null ? <Alerte message={erreur} /> : null}

        <button
          type="submit"
          disabled={envoi}
          style={{
            height: 52,
            border: 'none',
            borderRadius: 14,
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
            marginTop: 4,
          }}
        >
          {envoi ? 'Création…' : 'Créer mon compte'}
        </button>

        <p style={{ margin: 0, fontSize: 12, color: C.tertiaire, lineHeight: 1.5, textAlign: 'center' }}>
          En créant ton compte, tu acceptes la{' '}
          <LienTexte onClick={() => naviguer(CHEMIN_CONFIDENTIALITE)}>
            politique de confidentialité
          </LienTexte>
          .
        </p>
      </form>
    </CadreAuth>
  );
}
